import { until, gidToId } from '@/utils'
import { useBundleStore, useProductsStore } from '@/stores'

import type { Product, Variant, CartItem } from '@/types/shopify'

const OATS_OVERNIGHT = 'Oats Overnight'

export const useFueled = () => {

  const store = useProductsStore()
  const bundle = useBundleStore()

  const merchantCenterProductId = (pid: string | number, vid: string | number) => `shopify_US_${pid}_${vid}`

  const prepareProduct = (product: Product) => {
    const {
      id,
      sku,
      key,
      handle,
      title: name,
      type: category,
      flavorForwardImage,
      featured_image,
      tags
    } = product
    const [ productId, variantId ] = String(key || '/').split('/')
    return {
      productId: String(productId || gidToId(id)),
      variantId,
      variant: variantId,
      sku,
      name,
      category,
      url: `/products/${handle}`,
      brand: OATS_OVERNIGHT,
      imageUrl: flavorForwardImage?.url || featured_image,
      price: bundle.priceCents / 100,
      currency: 'USD',
      tags,
      merchantCenterProductId: merchantCenterProductId(productId, variantId)
    }
  }

  const collectProducts = () => {
    return store.filteredProducts.map((product, position) => {
      return {
        ...prepareProduct(product),
        position
      }
    })
  }

  const collectData = (event: string, data = {}) => {
    switch (event) {
      case 'productListViewed':
        return {
          category: store.filter,
          currency: 'USD',
          products: collectProducts()
        }
      default:
        return data
    }
  }


  const trackAddToCart = async (product?: Product, variant?: Variant | CartItem, quantity?: number, priceCents?: number) => {
    if (!(product && variant && quantity)) {
      return
    }
    const {
      id,
      title: variantTitle,
      sku
    } = variant
    const variantId = String(id)
    const data = {
      ...prepareProduct(product),
      variant: variantId,
      variantId,
      variantTitle,
      sku,
      quantity,
      price: Number(priceCents || variant.price) / 100
    }
    data.merchantCenterProductId = merchantCenterProductId(data.productId, data.variantId)
    await track('productAdded', data)
  }

  const trackProduct = async (id: Product | number | undefined, trackAction: string) => {
    const product = typeof id === 'number' ? store.productList.find(i => i.id === id) : id
    const position = store.filteredProducts.findIndex(i => i.id === product?.id)
    if (!product) {
      console.log('[fueled], product not found', product)
      return
    }
    await track(trackAction, { ...prepareProduct(product), position })
  }

  const productClicked = async (id: Product | number | undefined) => {
    trackProduct(id, 'productClicked')
  }

  const productViewed = async (id: Product | number | undefined) => {
    trackProduct(id, 'productViewed')
  }

  const bundleProductViewed = async () => {
    await until(() => bundle.ready)
    const { product, selectedVariant, priceCents } = bundle
    if (!product || !selectedVariant) {
      return
    }
    const {
      nid: pid,
      title: name
    } = product
    const {
      nid: vid,
      title: variantName,
      sku
    } = selectedVariant
    const data = {
      productId: String(pid),
      variant: String(vid),
      variantId: String(vid),
      variantName,
      sku,
      brand: OATS_OVERNIGHT,
      category: 'Listing',
      name,
      price: priceCents / 100.0,
      url: '/products',
      imageUrl: '//www.oatsovernight.com/cdn/shop/products/main-build-a-box.jpg?v=1677206296',
      currency: 'USD',
      collections: [
        'Customer Portal Products',
        'Listing'
      ],
      tags: [
        'best-seller',
        'custom-box',
        'meal-listing',
        'variety-pack'
      ],
      merchantCenterProductId: `shopify_US_${pid}_${vid}`
    }
    await track('productViewed', data)
  }

  const ensureFueledLoaded = async () => {
    if (window.fueled) {
      return Promise.resolve(true)
    }
    let tries = 0
    return new Promise((resolve) => {
      const int = setInterval(() => {
        tries++
        if (window.fueled) {
          clearInterval(int)
          resolve(true)
        } else if (tries > 100) {
          resolve(false)
        }
      }, 100)
    })
  }

  const track = async (event: string, data?: object) => {
    // if (DOMAIN.includes('dev')) {
    //   return Promise.resolve()
    // }
    try {
      await ensureFueledLoaded()
      if (typeof window.fueled?.[event] === 'function') {
        data = collectData(event, data)
        // console.log('[fueled]', event, data)
        await window.fueled[event](data)
      }
    } catch (e) {
      console.warn('[fueled error]', e)
    }
  }

  return {
    trackAddToCart,
    productClicked,
    productViewed,
    bundleProductViewed,
    track
  }
}
